/**
 * The chat side of an emailed reply (backend services/email/reply-in-chat).
 *
 * When a doctor answers an email Ellie drafted in a plan chat, the reply comes
 * back into that conversation as Ellie's turn with `metadata.email_reply`: the
 * doctor's words, what they mean, and, for a proposed time, the pending
 * confirm_appointment it can book. "Yes, confirm it" answers with a turn
 * carrying `metadata.email_confirmed`: the booked event, then a draft reply.
 * Both arrive live as a "Chat Turn" event on the plan's ingest channel.
 */

export type EmailReplyAction = {
  id: number;
  type: 'confirm_appointment';
  status: 'pending' | 'approved';
  title: string | null;
  date: string;
  time: string;
  /** "Tuesday, Oct 6 at 5:00 PM" */
  when: string;
};

export type EmailReply = {
  message_id: number;
  reply_to_message_id: number | null;
  /** "Dr. Clay" */
  from_name: string;
  from_email: string | null;
  subject: string | null;
  quote: string;
  /** Ellie's line under the quote, in Markdown. */
  text: string;
  action: EmailReplyAction | null;
};

export type EmailConfirmed = {
  message_id: number;
  action_id: number | null;
  event: { event_id?: string; start?: string; end?: string; title: string; when: string | null };
};

export const CHAT_TURN_EVENT = 'Chat Turn';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isConversationId = (value: unknown): value is string => typeof value === 'string' && UUID.test(value);

const parsed = (metadata: unknown): Record<string, any> => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata as Record<string, any>;
  try { return JSON.parse(String(metadata)) || {}; } catch { return {}; }
};

export function emailReplyFromMetadata(metadata: unknown): EmailReply | null {
  const reply = parsed(metadata).email_reply;
  if (!reply || typeof reply !== 'object' || !Number.isInteger(reply.message_id) || typeof reply.quote !== 'string') return null;
  return reply as EmailReply;
}

export function emailConfirmedFromMetadata(metadata: unknown): EmailConfirmed | null {
  const confirmed = parsed(metadata).email_confirmed;
  if (!confirmed || typeof confirmed !== 'object' || !confirmed.event?.title) return null;
  return confirmed as EmailConfirmed;
}

/**
 * A turn row from a live event or the confirm response, in the shape
 * get-conversation returns it, so it merges into the saved conversation
 * (ADD_CONVERSATION) and restores like any other turn.
 */
export function savedTurnRow(turn: Record<string, any>): Record<string, any> {
  return {
    ...turn,
    metadata: parsed(turn.metadata),
    mentions: Array.isArray(turn.mentions) ? turn.mentions : [],
    raw: turn.raw != null && turn.raw !== false,
    is_shallow: false,
  };
}

/** The reply's saved turn with its card marked handled, as the server marks it on confirm. */
export function withReplyHandled(turn: Record<string, any>): Record<string, any> {
  const metadata = parsed(turn.metadata);
  if (!metadata.email_reply?.action) return turn;
  return {
    ...turn,
    metadata: { ...metadata, email_reply: { ...metadata.email_reply, action: { ...metadata.email_reply.action, status: 'approved' } } },
  };
}

/**
 * The confirmed turn around its event card: the lead ("It's on Ryver's
 * calendar:") above it, and the rest, the reply draft, below it. The booking
 * itself is in the card, so the lead drops what follows its colon.
 */
export function splitBookedLead(content: string): { lead: string; rest: string } {
  const [first = '', ...others] = String(content || '').split('\n\n');
  const colon = first.indexOf(':');
  return { lead: colon >= 0 ? first.slice(0, colon + 1) : first, rest: others.join('\n\n') };
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Tuesday, Oct 6 at 5 PM" for a wall-clock date and time ("2026-10-06",
 * "17:00"), the doctor's own, read as written and never shifted by a zone.
 */
export function appointmentShort(date: unknown, time: unknown): string | null {
  const d = String(date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = String(time || '').match(/^(\d{2}):(\d{2})$/);
  if (!d || !t) return null;
  const day = new Date(Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3])));
  if (Number.isNaN(day.getTime()) || day.getUTCDate() !== Number(d[3])) return null;
  const hour = Number(t[1]);
  const minute = Number(t[2]);
  if (hour > 23 || minute > 59) return null;
  const clock = `${hour % 12 === 0 ? 12 : hour % 12}${minute ? `:${t[2]}` : ''} ${hour < 12 ? 'AM' : 'PM'}`;
  return `${WEEKDAYS[day.getUTCDay()]}, ${MONTHS[day.getUTCMonth()]} ${day.getUTCDate()} at ${clock}`;
}

type PendingActionForReply = {
  id: number;
  action_type: string;
  status?: string | null;
  source_agent?: string | null;
  payload?: Record<string, any> | null;
  expires_at?: string | null;
};

/**
 * A pending appointment a doctor offered by email, in reply to an email sent
 * from an Ellie conversation: what Ellie home's opener says about it. Null for
 * anything else (and for an appointment emailed out of the blue: with no
 * conversation to answer in, it stays in Ellie's notes).
 */
export function emailedAppointment(action: PendingActionForReply, now = Date.now()): {
  actionId: number; conversationId: string; messageId: number | null; fromName: string; when: string;
} | null {
  if (action.action_type !== 'confirm_appointment' || action.status !== 'pending' || action.source_agent !== 'email_extraction') return null;
  if (action.expires_at && new Date(action.expires_at).getTime() <= now) return null;
  const payload = action.payload || {};
  const when = appointmentShort(payload.date ?? payload.start_date, payload.time ?? payload.start_time);
  if (!when || !isConversationId(payload.conversation_id)) return null;
  const fromName = String(payload.from_name || payload.from_email || '').trim();
  if (!fromName) return null;
  return {
    actionId: action.id,
    conversationId: payload.conversation_id,
    messageId: Number.isInteger(payload.message_id) ? payload.message_id : null,
    fromName,
    when,
  };
}
